import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { AlertTriangle, Check, ChevronRight, ExternalLink, Sparkles } from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { supabase, fetchAllRecords } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { isApiError, toUserError } from "@/lib/api-error";
import { linkedInApi } from "@/services/linkedin-api";
import {
  AUTO_REVIEW_CODE_LABELS,
  CONFIANCA_REVISAO_IA_LABELS,
  RECUSA_REVISAO_IA_LABELS,
  SITUACAO_REVISAO_IA_LABELS,
  AUTO_REVIEW_FREELANCER_CODES,
  jobTemSuspeitaFreelancer,
  type CarreiraObservacoes,
  type ExperienceItem,
  type LinkedInJob,
  type SkillItem,
} from "@/types/linkedin";
import { cn } from "@/lib/utils";
import { formatDateOnly } from "@/utils/dateUtils";
import { ChecklistDaRevisao, useSlotDaChecklist, type ItemDaRevisao } from "./ChecklistDaRevisao";
import { useAbrirPainelDaLeitura } from "./LinkedInRevisaoComPerfil";

/** Resposta do botão "É freelancer / Não é" (override confirmacao_vinculo). */
type ConfirmacaoVinculo = "freelancer" | "nao_freelancer";

/** Seções do formulário que abrem, fecham e se marcam como revisadas. */
type SecaoId = "situacao" | "resumo" | "trajetoria" | "areas" | "experiencias" | "skills" | "formacao";

/** Razões do classificador que viram item da checklist; as outras só explicam a revisão. */
const CODIGOS_COM_ACAO = new Set([
  "sinal_freelancer",
  "empresa_propria",
  "multiplas_empresas_atuais",
  "conflito_linkedin_url",
  "atual_a_confirmar",
  "extracao_incompleta",
]);

const VINCULO_LABELS: Record<NonNullable<ExperienceItem["vinculo_tipo"]>, string> = {
  empregado: "empregado(a)",
  freelance: "freelance",
  empresa_propria: "empresa própria",
};

/** Contexto de revisão em fila (lote): habilita "Aplicar e Próximo" + "Pular". */
export interface SequentialReviewContext {
  /** Posição atual na fila (1-based). */
  current: number;
  /** Total de itens na fila. */
  total: number;
  /** Pula este item (joga pro fim da fila) sem aplicar. */
  onSkip: () => void;
}

interface Props {
  job: LinkedInJob | null;
  /**
   * Chamado após aplicar com sucesso (modo single: volta pra lista; modo
   * sequencial: o pai avança pro próximo) e pelo botão Cancelar no modo single.
   */
  onClose: () => void;
  /**
   * Presente => revisão em fila (lote): troca o rodapé por Pular + "Aplicar e
   * Próximo/Finalizar". Ausente => comportamento single de sempre.
   */
  sequential?: SequentialReviewContext;
  /**
   * Modo visualização (jobs já aplicados / sem mudanças): mostra tudo que a IA
   * extraiu + o diff, sem checkboxes, sem editores e sem botão Aplicar. Exibe
   * a proveniência do apply (automático com razões vs revisado manualmente).
   */
  readOnly?: boolean;
}

/**
 * Conteúdo da revisão de um upload de LinkedIn (JSON extraído com diff).
 * Usado na página dedicada (/linkedin/jobs/:jobId/revisar) e na revisão
 * sequencial por lote (/linkedin/lote/:batchId/revisar).
 */
export function LinkedInReviewContent({ job, onClose, sequential, readOnly }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Override: keys EXCLUÍDAS (default todos incluídos). O payload é construído
  // como `include_*_keys` (lista do que entra) na hora de aplicar.
  const [excludedExpKeys, setExcludedExpKeys] = useState<Set<string>>(new Set());
  const [excludedSkillKeys, setExcludedSkillKeys] = useState<Set<string>>(new Set());

  // Confirmação humana do "atual?" inferido + edição da data de término. Chave =
  // expKey → estado efetivo escolhido na revisão. Vazio = usa o que a IA extraiu.
  const [expOverrides, setExpOverrides] = useState<Map<string, ExpOverride>>(new Map());

  // Resposta do botão "É freelancer / Não é". Só viaja no apply quando o
  // revisor clicar de fato (clique = confirmação humana, vira sinal de
  // disponibilidade confirmado). undefined = não respondeu.
  const [confirmacaoVinculo, setConfirmacaoVinculo] = useState<
    ConfirmacaoVinculo | undefined
  >(undefined);

  // Itens da checklist sem ação no formulário: o revisor marca que conferiu.
  const [conflitoConferido, setConflitoConferido] = useState(false);
  const [extracaoConferida, setExtracaoConferida] = useState(false);

  // Seções: só o que o revisor mexeu. Sem escolha, o que entra automático
  // começa revisado e fechado, e o que pede resposta começa aberto.
  const [secoesAbertas, setSecoesAbertas] = useState<Partial<Record<SecaoId, boolean>>>({});
  const [secoesRevisadas, setSecoesRevisadas] = useState<Partial<Record<SecaoId, boolean>>>({});

  const slotDaChecklist = useSlotDaChecklist();
  const abrirPainelDaLeitura = useAbrirPainelDaLeitura();

  // Catálogo canônico de áreas (areas_atuacao). As áreas sugeridas pela IA só
  // são aplicadas quando "batem" com este catálogo (request do produto).
  const { data: areaCatalog = [] } = useQuery({
    queryKey: ["areas-atuacao-catalog"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("areas_atuacao")
        .select("nome")
        .or("is_active.is.null,is_active.eq.true");
      if (error) throw error;
      return (data ?? [])
        .map((r) => r.nome as string)
        .filter(Boolean);
    },
    staleTime: 60 * 60 * 1000,
  });

  // Áreas atuais do freelancer (antes) — pra mostrar antes/depois.
  const { data: currentAreas = [] } = useQuery({
    queryKey: ["freelancer-areas", job?.freelancer_id],
    enabled: !!job?.freelancer_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("freelancers")
        .select("areas")
        .eq("id", job!.freelancer_id!)
        .single();
      if (error) throw error;
      return ((data?.areas ?? []) as string[]).filter(Boolean);
    },
  });

  const areasView = useMemo(
    () => computeAreasView(job?.extracted_json?.areas_sugeridas ?? [], currentAreas, areaCatalog),
    [job?.extracted_json?.areas_sugeridas, currentAreas, areaCatalog],
  );

  // Slugs das empresas já cadastradas — pra marcar cada experiência como "no
  // banco" (vincula) ou "nova" (será criada no apply). Best-effort: match por
  // slug exato (o resolve_empresa do backend ainda faz fuzzy por cima).
  const { data: empresaSlugs = new Set<string>() } = useQuery({
    queryKey: ["empresas-slugs"],
    queryFn: async () => {
      const rows = await fetchAllRecords<{ slug: string | null }>("empresas", "slug");
      return new Set(rows.map((r) => r.slug).filter(Boolean) as string[]);
    },
    staleTime: 60 * 60 * 1000,
  });

  const invalidateAfterApply = () => {
    queryClient.invalidateQueries({ queryKey: ["linkedin-jobs"] });
    queryClient.invalidateQueries({ queryKey: ["freelancer", job?.freelancer_id] });
    queryClient.invalidateQueries({ queryKey: ["freelancer-areas", job?.freelancer_id] });
    queryClient.invalidateQueries({ queryKey: ["freelancer-linkedin"] });
    // O apply emite sinal de disponibilidade (situacao_emprego) no backend.
    queryClient.invalidateQueries({ queryKey: ["disponibilidade"] });
  };

  const apply = useMutation({
    mutationFn: () => {
      const extracted = job!.extracted_json!;
      const includedExpKeys = extracted.experiencias
        .map((e) => expKey(e))
        .filter((k) => !excludedExpKeys.has(k));
      const includedSkillKeys = extracted.skills
        .map((s) => slugify(s.nome))
        .filter((k) => !excludedSkillKeys.has(k));
      const expExcluded = excludedExpKeys.size > 0;
      const skillExcluded = excludedSkillKeys.size > 0;
      const experienciaOverrides = Object.fromEntries(
        [...expOverrides].map(([key, o]) => [
          key,
          { is_atual: o.is_atual, data_fim: o.is_atual ? null : o.data_fim },
        ]),
      );
      // Sempre manda áreas reconciliadas: só as do catálogo que ainda não estão
      // no freelancer. Itens "fora do catálogo" ficam de fora.
      const overrides: Record<string, unknown> = {
        areas_sugeridas: areasView.toApply,
        ...(expExcluded ? { include_experiencia_keys: includedExpKeys } : {}),
        ...(skillExcluded ? { include_skill_keys: includedSkillKeys } : {}),
        ...(expOverrides.size > 0 ? { experiencia_overrides: experienciaOverrides } : {}),
        ...(confirmacaoVinculo ? { confirmacao_vinculo: confirmacaoVinculo } : {}),
      };
      return linkedInApi.apply(job!.id, { overrides });
    },
    onSuccess: () => {
      toast({ title: "Aplicado com sucesso", description: "Perfil do freelancer atualizado." });
      invalidateAfterApply();
      onClose();
    },
    onError: async (e: Error) => {
      // 409 aqui costuma significar que o job JÁ foi aplicado por outra
      // aba/sessão concorrente — o backend recusa um 2º apply pro mesmo job
      // (guard de status), mas isso não é um erro de dados: o 1º apply deu
      // certo. Confere o estado atual antes de assustar com toast vermelho
      // (COR-356: "Erro ao aplicar" apareceu num job que já tinha aplicado).
      if (isApiError(e) && e.status === 409) {
        try {
          const current = await linkedInApi.getJob(job!.id);
          if (current.status !== "review_ready") {
            toast({
              title: "Job já processado",
              description:
                "Este upload já tinha sido aplicado (provavelmente em outra aba). Nada a fazer aqui.",
            });
            invalidateAfterApply();
            onClose();
            return;
          }
        } catch {
          // releitura falhou — cai no toast de erro genérico abaixo
        }
      }
      toast({ title: "Erro ao aplicar", description: toUserError(e).description, variant: "destructive" });
    },
  });

  const expState = useMemo(() => computeExpState(job), [job]);
  const skillState = useMemo(() => computeSkillState(job), [job]);

  const toggleExp = (key: string) =>
    setExcludedExpKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const setExpOverride = (key: string, override: ExpOverride) =>
    setExpOverrides((prev) => {
      const next = new Map(prev);
      next.set(key, override);
      return next;
    });

  const toggleSkill = (key: string) =>
    setExcludedSkillKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (!job || !job.extracted_json) return null;

  const extracted = job.extracted_json;
  const diff = job.diff_summary;
  const counts = diff?.counts;
  const isFirstUpload = !!diff?.is_first;
  const perfilChanges = (diff?.fields_changed ?? []).filter(
    (change) => !PROFILE_INTERNAL_FIELDS.has(change.field),
  );
  // Contagem do que fica visível: em diffs anteriores à COR-367, counts.changed
  // ainda inclui campos internos, então a conta sai do que ganhou selo.
  const visibleChanged = [...expState.values()].filter((info) => info.state === "changed").length;
  const comparadoCom = diff?.previous_applied_at
    ? `Comparado com o último LinkedIn aplicado em ${formatDateOnly(diff.previous_applied_at)}.`
    : "Comparado com o último LinkedIn aplicado.";
  const conflict = job.linkedin_url_conflict;
  const showConflict = !!conflict && conflict.freelancer_id !== job.freelancer_id;
  const totalExps = extracted.experiencias.length;
  const totalSkills = extracted.skills.length;
  const includedExpCount = totalExps - excludedExpKeys.size;
  const includedSkillCount = totalSkills - excludedSkillKeys.size;
  const hasOverrides = excludedExpKeys.size > 0 || excludedSkillKeys.size > 0;

  // Razões do classificador (por que este job precisou de revisão manual).
  const razoesRevisao =
    job.auto_review?.mode === "manual" ? job.auto_review.reasons : [];
  // Suspeita de freelancer/empresa própria HOJE: pelo classificador ou, em
  // jobs sem auto_review, pela conclusão da extração. Sinais só históricos
  // (is_freelancer_declarado=false) não travam o Aplicar — os bullets ainda
  // aparecem no bloco abaixo como contexto.
  const temSuspeitaFreelancer =
    jobTemSuspeitaFreelancer(job) || extracted.is_freelancer_declarado === true;
  // Resposta da revisão por IA que não bastou para aplicar sozinha. Só
  // sugere: o clique no botão continua sendo a confirmação humana.
  const revisaoIa = job.status === "review_ready" ? job.auto_review?.revisao_ia : undefined;
  const sugestaoIa: ConfirmacaoVinculo | undefined =
    revisaoIa?.situacao === "freelancer"
      ? "freelancer"
      : revisaoIa?.situacao === "empregado"
      ? "nao_freelancer"
      : undefined;

  // Posições atuais como ficam depois da revisão: a evidência da situação de trabalho.
  const posicoesAtuais = sortByDate(extracted.experiencias).filter((experiencia) => {
    const key = expKey(experiencia);
    if (excludedExpKeys.has(key)) return false;
    return expOverrides.get(key)?.is_atual ?? experiencia.is_atual;
  });
  const empresasEmpregado = [
    ...new Set(
      posicoesAtuais
        .filter((experiencia) => experiencia.vinculo_tipo !== "freelance" && experiencia.vinculo_tipo !== "empresa_propria")
        .map((experiencia) => experiencia.empresa_nome),
    ),
  ];
  const empresaDoEmprego =
    revisaoIa?.empresa_principal ?? (empresasEmpregado.length === 1 ? empresasEmpregado[0] : null);
  const rotuloNaoFreelancer = empresaDoEmprego ? `Empregado(a) em ${empresaDoEmprego}` : "Não é freelancer";
  // A revisão da IA e o vínculo lido em cada posição atual podem discordar
  // (ex.: "empregado em X" com X lida como empresa própria). Aí a sugestão
  // não ganha destaque, para o clique não virar confirmação humana sem olhar.
  const posicaoIndependente = posicoesAtuais.some(
    (experiencia) => experiencia.vinculo_tipo === "freelance" || experiencia.vinculo_tipo === "empresa_propria",
  );
  const leiturasDivergem =
    (sugestaoIa === "nao_freelancer" && posicaoIndependente) ||
    (sugestaoIa === "freelancer" &&
      posicoesAtuais.length > 0 &&
      posicoesAtuais.every((experiencia) => experiencia.vinculo_tipo === "empregado"));
  // A sugestão da IA vem primeiro e é a ação principal; a outra fica ao lado.
  const ordemDasRespostas: ConfirmacaoVinculo[] =
    sugestaoIa === "freelancer" ? ["freelancer", "nao_freelancer"] : ["nao_freelancer", "freelancer"];
  const rotuloResposta: Record<ConfirmacaoVinculo, string> = {
    freelancer: "Freelancer ou empresa própria",
    nao_freelancer: rotuloNaoFreelancer,
  };
  // Por que a pergunta apareceu: razões do classificador e sinais da extração, sem repetir.
  const motivosDaPergunta = (job.auto_review?.reasons ?? [])
    .filter((razao) => AUTO_REVIEW_FREELANCER_CODES.has(razao.code))
    .map((razao) => razao.detail);
  for (const sinal of extracted.sinais_freelancer ?? []) {
    if (!motivosDaPergunta.some((motivo) => motivo.includes(sinal))) motivosDaPergunta.push(sinal);
  }

  const emRevisao = !readOnly && job.status === "review_ready";
  const razaoExtracaoIncompleta = razoesRevisao.find((razao) => razao.code === "extracao_incompleta");
  const expsAConfirmar = extracted.experiencias.filter((experiencia) => experiencia.is_atual_a_confirmar);
  const itensRevisao: ItemDaRevisao[] = [];
  if (!readOnly && showConflict && conflict) {
    itensRevisao.push({
      id: "revisao-conflito",
      rotulo: "LinkedIn já cadastrado em outro freelancer",
      detalhe: conflict.nome,
      resolvido: conflitoConferido,
      onMarcar: setConflitoConferido,
    });
  }
  if (!readOnly && temSuspeitaFreelancer) {
    itensRevisao.push({
      id: "revisao-situacao",
      rotulo: "Situação de trabalho",
      detalhe: confirmacaoVinculo ? rotuloResposta[confirmacaoVinculo] : undefined,
      resolvido: !!confirmacaoVinculo,
    });
  }
  if (!readOnly) {
    for (const experiencia of expsAConfirmar) {
      const key = expKey(experiencia);
      itensRevisao.push({
        id: `revisao-exp-${key}`,
        rotulo: `Emprego atual em ${experiencia.empresa_nome}`,
        detalhe: experiencia.cargo_nome,
        resolvido: expOverrides.has(key) || excludedExpKeys.has(key),
      });
    }
  }
  if (emRevisao && razaoExtracaoIncompleta) {
    itensRevisao.push({
      id: "revisao-topo",
      rotulo: "Conferir a extração incompleta",
      detalhe: razaoExtracaoIncompleta.detail,
      resolvido: extracaoConferida,
      onMarcar: setExtracaoConferida,
    });
  }
  const contextoRevisao = [
    ...new Set(
      razoesRevisao
        .filter((razao) => !CODIGOS_COM_ACAO.has(razao.code))
        .map((razao) => AUTO_REVIEW_CODE_LABELS[razao.code] ?? razao.detail),
    ),
  ];
  const pendentes = itensRevisao.filter((item) => !item.resolvido);
  const mostraChecklist = !readOnly && (itensRevisao.length > 0 || (emRevisao && contextoRevisao.length > 0));

  // Só pede olho a seção com item na checklist; o resto entra automático.
  const aRevisar: Record<SecaoId, boolean> = {
    situacao: temSuspeitaFreelancer,
    resumo: false,
    trajetoria: false,
    areas: false,
    experiencias: expsAConfirmar.length > 0,
    skills: false,
    formacao: false,
  };
  const resumoMudou = perfilChanges.some((change) => change.field === "resumo");
  const novasExperiencias = [...expState.values()].filter((info) => info.state === "new").length;
  const avisoAutomatico: Record<SecaoId, string> = {
    situacao: "sem sinais de freelancer",
    resumo: isFirstUpload || resumoMudou ? "entra como está" : "não mudou",
    trajetoria: "calculada",
    areas:
      areasView.toApply.length > 0
        ? `entra ${areasView.toApply.join(", ")}`
        : areasView.hasOffCatalog
        ? "sugestões fora do catálogo ficam de fora"
        : "nada novo",
    experiencias:
      `${includedExpCount} ${includedExpCount === 1 ? "entra" : "entram"}` +
      (novasExperiencias > 0 ? `, ${novasExperiencias} ${novasExperiencias === 1 ? "nova" : "novas"}` : ""),
    skills: `${includedSkillCount} ${includedSkillCount === 1 ? "entra" : "entram"}`,
    formacao: `${extracted.formacao.length} ${extracted.formacao.length === 1 ? "entra" : "entram"}`,
  };
  const secaoRevisada = (secao: SecaoId) => secoesRevisadas[secao] ?? !aRevisar[secao];
  const secaoAberta = (secao: SecaoId) => secoesAbertas[secao] ?? !secaoRevisada(secao);
  const abrirSecao = (secao: SecaoId, aberta: boolean) =>
    setSecoesAbertas((anterior) => ({ ...anterior, [secao]: aberta }));
  const marcarRevisada = (secao: SecaoId, revisada: boolean) => {
    setSecoesRevisadas((anterior) => ({ ...anterior, [secao]: revisada }));
    setSecoesAbertas((anterior) => ({ ...anterior, [secao]: !revisada }));
  };
  /** Props de uma seção: em modo visualização ela fica sempre aberta, sem controles. */
  const secao = (id: SecaoId) =>
    readOnly
      ? {}
      : {
          aberta: secaoAberta(id),
          onAbertaChange: (aberta: boolean) => abrirSecao(id, aberta),
          revisada: secaoRevisada(id),
          onRevisadaChange: (revisada: boolean) => marcarRevisada(id, revisada),
          aviso: aRevisar[id] ? undefined : avisoAutomatico[id],
          pelaIa: !aRevisar[id] && secoesRevisadas[id] === undefined,
        };
  const irPara = (id: string) => {
    if (id === "revisao-situacao") abrirSecao("situacao", true);
    if (id.startsWith("revisao-exp-")) abrirSecao("experiencias", true);
    requestAnimationFrame(() =>
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" }),
    );
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div id="revisao-topo" className="space-y-2">
        <div>
          <h1 className="text-xl font-bold leading-tight">{extracted.nome_completo}</h1>
          {(extracted.headline || extracted.localizacao) && (
            <p className="text-sm text-muted-foreground">
              {extracted.headline && <span>{extracted.headline}</span>}
              {extracted.localizacao && (
                <span className="ml-2">· {extracted.localizacao}</span>
              )}
            </p>
          )}
          <div className="mt-1 flex items-center gap-3">
            {job.s3_url && (
              <a
                href={job.s3_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-fit items-center text-xs text-primary hover:underline"
              >
                Ver PDF do LinkedIn <ExternalLink className="ml-1 h-3 w-3" />
              </a>
            )}
            {job.freelancer_id && (
              <button
                type="button"
                onClick={() => abrirPainelDaLeitura(job.freelancer_id!)}
                className="inline-flex w-fit items-center gap-1 text-xs text-primary hover:underline xl:hidden"
              >
                <Linkedin className="h-3 w-3" />
                Ver como no LinkedIn
              </button>
            )}
          </div>
        </div>

        {/* Banner de conflito de linkedin_url */}
        {showConflict && conflict && (
          <div id="revisao-conflito" className="flex items-start gap-2 rounded-md border px-3 py-2 text-sm">
            <AlertTriangle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
            <div className="flex-1">
              <p>
                Este LinkedIn já está cadastrado em{" "}
                <Link
                  to={`/freelancers/${conflict.freelancer_id}`}
                  className="font-semibold underline hover:no-underline"
                  target="_blank"
                >
                  {conflict.nome}
                </Link>
                {conflict.email ? (
                  <span className="text-muted-foreground"> ({conflict.email})</span>
                ) : null}
                . Possível duplicata.
              </p>
              {!readOnly && (
                <label className="mt-1.5 flex w-fit items-center gap-2 text-xs">
                  <Checkbox
                    checked={conflitoConferido}
                    onCheckedChange={(marcado) => setConflitoConferido(marcado === true)}
                  />
                  Conferi: é outra pessoa, pode aplicar
                </label>
              )}
            </div>
          </div>
        )}

        {/* Proveniência do apply (modo visualização de job aplicado) */}
        {job.status === "applied" && job.review_mode === "auto" && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
            <p className="font-medium text-emerald-900">Aplicado automaticamente</p>
            {(job.auto_review?.reasons?.length ?? 0) > 0 && (
              <ul className="mt-1 list-disc list-inside space-y-0.5 text-emerald-900">
                {job.auto_review!.reasons.map((razao, indice) => (
                  <li key={indice} title={razao.detail}>
                    {razao.code === "revisao_ia"
                      ? razao.detail
                      : AUTO_REVIEW_CODE_LABELS[razao.code] ?? razao.detail}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {job.status === "applied" && job.review_mode === "human" && (
          <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
            Revisado e aplicado manualmente.
          </div>
        )}

        {/* Banner de diff */}
        {diff && (
          <div className="flex flex-wrap items-center gap-2 pt-1 text-sm">
            {isFirstUpload ? (
              <Badge className="bg-blue-100 text-blue-800">Primeiro LinkedIn deste freelancer</Badge>
            ) : counts ? (
              <>
                {counts.added > 0 && (
                  <Badge className="bg-green-100 text-green-800">
                    {counts.added} {counts.added === 1 ? "item novo" : "itens novos"}
                  </Badge>
                )}
                {visibleChanged > 0 && (
                  <Badge className="bg-yellow-100 text-yellow-800">
                    {visibleChanged} {visibleChanged === 1 ? "experiência mudou" : "experiências mudaram"}
                  </Badge>
                )}
                {counts.removed > 0 && (
                  <Badge className="bg-red-100 text-red-800">
                    {counts.removed} {counts.removed === 1 ? "item saiu" : "itens saíram"}
                  </Badge>
                )}
                {perfilChanges.length > 0 && (
                  <Badge className="bg-yellow-100 text-yellow-800">
                    {perfilChanges.length === 1 ? "1 campo do perfil mudou" : `${perfilChanges.length} campos do perfil mudaram`}
                  </Badge>
                )}
                {counts.added + visibleChanged + counts.removed + perfilChanges.length === 0 && (
                  <Badge className="bg-muted text-muted-foreground">
                    Nada mudou no LinkedIn; só o texto extraído foi reescrito
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">{comparadoCom}</span>
              </>
            ) : null}
          </div>
        )}

        {/* Campos do perfil (headline, resumo, localização) que mudaram */}
        {!isFirstUpload && perfilChanges.length > 0 && (
          <div className="rounded-md border px-3 py-2 text-sm">
            <p className="text-xs font-medium text-muted-foreground mb-1">Mudou no perfil</p>
            <ul className="space-y-1">
              {perfilChanges.map((change) => (
                <li key={change.field}>
                  <BeforeAfter
                    label={PROFILE_FIELD_LABELS[change.field] ?? change.field}
                    before={formatProfileValue(change.before)}
                    after={formatProfileValue(change.after)}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="space-y-6 py-4">
        {/* Situação de trabalho: confirmação humana de freelancer/empresa própria */}
        <Section
          id="revisao-situacao"
          title="Situação de trabalho"
          status={
            readOnly ? undefined : confirmacaoVinculo ? "respondida" : temSuspeitaFreelancer ? "pendente" : undefined
          }
          {...secao("situacao")}
        >
          {readOnly ? (
            <p className="text-sm">
              {extracted.is_freelancer_declarado === true
                ? "Identificado como freelancer"
                : extracted.is_freelancer_declarado === false
                ? "Não identificado como freelancer"
                : "Sem conclusão sobre atuação como freelancer"}
              {extracted.vinculo_confirmado_humano && (
                <span className="ml-1 text-muted-foreground">(confirmado na revisão)</span>
              )}
            </p>
          ) : (
            <div className="space-y-3">
              {posicoesAtuais.length > 0 ? (
                <div className="space-y-2">
                  {posicoesAtuais.map((experiencia, indice) => (
                    <PosicaoAtual key={indice} experiencia={experiencia} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhuma posição atual no LinkedIn.</p>
              )}
              <div>
                <p className="text-sm font-medium">Hoje, como trabalha?</p>
                {leiturasDivergem && !confirmacaoVinculo && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    As leituras da IA não batem: a sugestão diz uma coisa e o vínculo da posição atual diz outra. Confira o cargo antes de responder.
                  </p>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  {ordemDasRespostas.map((resposta) => {
                    const escolhida = confirmacaoVinculo === resposta;
                    const destaque = confirmacaoVinculo ? escolhida : sugestaoIa === resposta;
                    return (
                      <Button
                        key={resposta}
                        variant={destaque ? "default" : "secondary"}
                        className={cn("h-auto whitespace-normal py-2 text-left", confirmacaoVinculo && !escolhida && "opacity-60")}
                        onClick={() => setConfirmacaoVinculo(resposta)}
                      >
                        {escolhida && <Check className="mr-1.5 h-4 w-4 shrink-0" />}
                        <span>
                          {rotuloResposta[resposta]}
                          {sugestaoIa === resposta && (
                            <span className="ml-1.5 text-xs font-normal opacity-80">sugestão da IA</span>
                          )}
                        </span>
                      </Button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  A resposta fica registrada no perfil e atualiza a disponibilidade do freelancer.
                </p>
              </div>
              {(motivosDaPergunta.length > 0 || revisaoIa) && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer select-none hover:text-foreground">
                    Por que a pergunta apareceu
                  </summary>
                  <ul className="mt-1.5 list-disc space-y-0.5 pl-4">
                    {motivosDaPergunta.map((motivo, indice) => (
                      <li key={indice}>{motivo}</li>
                    ))}
                    {revisaoIa?.situacao && revisaoIa.confianca && (
                      <li>
                        Leitura da IA: {SITUACAO_REVISAO_IA_LABELS[revisaoIa.situacao]}
                        {revisaoIa.empresa_principal && ` em ${revisaoIa.empresa_principal}`} (confiança{" "}
                        {CONFIANCA_REVISAO_IA_LABELS[revisaoIa.confianca]})
                        {revisaoIa.evidencia && `: ${revisaoIa.evidencia}`}
                      </li>
                    )}
                    {revisaoIa?.recusa && RECUSA_REVISAO_IA_LABELS[revisaoIa.recusa] && (
                      <li>Não foi aplicado sozinho porque {RECUSA_REVISAO_IA_LABELS[revisaoIa.recusa]}.</li>
                    )}
                  </ul>
                </details>
              )}
            </div>
          )}
        </Section>

        {extracted.resumo && (
          <Section title="Resumo" {...secao("resumo")}>
            <p className="text-sm whitespace-pre-wrap">{extracted.resumo}</p>
          </Section>
        )}

        {/* Trajetória profissional */}
        {(extracted.carreira || extracted.anos_experiencia_total != null) && (
          <Section title="Trajetória profissional" {...secao("trajetoria")}>
            <div className="bg-muted/50 p-3 rounded space-y-2 text-sm">
              {extracted.anos_experiencia_total != null && (
                <div>
                  <span className="font-medium">
                    ≈ {Math.round(extracted.anos_experiencia_total)}{" "}
                    {Math.round(extracted.anos_experiencia_total) === 1 ? "ano" : "anos"}
                  </span>{" "}
                  <span className="text-muted-foreground">de experiência no total</span>
                </div>
              )}
              {extracted.carreira?.padrao_carreira && (
                <Badge variant="outline">
                  {padraoCarreiraLabel(extracted.carreira.padrao_carreira)}
                </Badge>
              )}
              {extracted.carreira?.observacoes_texto?.length ? (
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  {extracted.carreira.observacoes_texto.map((bullet, i) => (
                    <li key={i}>{bullet}</li>
                  ))}
                </ul>
              ) : null}
              {extracted.carreira && hasTenure(extracted.carreira) && (
                <div className="grid grid-cols-3 gap-3 pt-2 border-t">
                  <TenureStat
                    label="Tempo médio por empresa"
                    meses={extracted.carreira.tenure_medio_meses}
                  />
                  <TenureStat
                    label="Maior período"
                    meses={extracted.carreira.tenure_maior_meses}
                  />
                  <TenureStat
                    label="No emprego atual"
                    meses={extracted.carreira.tenure_atual_meses}
                  />
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Áreas — antes (atuais) / sugeridas / depois (resultado final) */}
        {(areasView.current.length > 0 || areasView.itens.length > 0) && (
          <Section title="Áreas de atuação" {...secao("areas")}>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Atuais</div>
                {areasView.current.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {areasView.current.map((a) => (
                      <Badge key={a} variant="secondary">{a}</Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">Nenhuma cadastrada</span>
                )}
              </div>

              {areasView.itens.length > 0 && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Sugeridas pela IA</div>
                  <div className="flex flex-wrap gap-1">
                    {areasView.itens.map((it, i) =>
                      it.already ? (
                        <Badge key={i} variant="outline" className="opacity-60">
                          {it.canonical} · já cadastrada
                        </Badge>
                      ) : it.inCatalog ? (
                        <Badge key={i} className="bg-green-100 text-green-800 border-transparent">
                          + {it.canonical}
                        </Badge>
                      ) : (
                        <Badge
                          key={i}
                          variant="outline"
                          className="border-amber-300 text-amber-700"
                        >
                          {it.raw} · fora do catálogo
                        </Badge>
                      ),
                    )}
                  </div>
                  {areasView.hasOffCatalog && (
                    <p className="text-[11px] text-amber-700 mt-1">
                      Áreas fora do catálogo não serão aplicadas.
                    </p>
                  )}
                </div>
              )}

              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  Resultado final (atuais + sugeridas)
                </div>
                <div className="flex flex-wrap gap-1">
                  {areasView.final.map((a) => {
                    const isNew = areasView.toApplyNormSet.has(slugify(a));
                    return (
                      <Badge
                        key={a}
                        variant={isNew ? "outline" : "secondary"}
                        className={isNew ? "bg-green-100 text-green-800 border-transparent" : ""}
                      >
                        {a}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </div>
          </Section>
        )}

        {/* Experiências */}
        <Section
          title={
            readOnly
              ? `Experiências (${totalExps})`
              : `Experiências (${includedExpCount} de ${totalExps} selecionadas)`
          }
          {...secao("experiencias")}
        >
          <p className="text-xs text-muted-foreground mb-2 -mt-1">
            {isFirstUpload
              ? "Primeiro LinkedIn deste freelancer: todas as experiências serão cadastradas."
              : diff
              ? comparadoCom
              : null}
            {!readOnly && " Cada empresa será vinculada (ou criada) no banco ao aplicar."}
          </p>
          <div className="space-y-2">
            {sortByDate(extracted.experiencias).map((e, i) => {
              const key = expKey(e);
              return (
                <ExperienceRow
                  key={i}
                  id={`revisao-exp-${key}`}
                  exp={e}
                  diff={expState.get(key)}
                  excluded={readOnly ? false : excludedExpKeys.has(key)}
                  onToggleExclude={readOnly ? undefined : () => toggleExp(key)}
                  empresaNoBanco={empresaSlugs.has(slugify(e.empresa_nome))}
                  override={readOnly ? undefined : expOverrides.get(key)}
                  onSetOverride={
                    readOnly ? undefined : (o) => setExpOverride(key, o)
                  }
                />
              );
            })}
            {/* Removidas (vinham do anterior) — não selecionáveis */}
            {diff?.removed.experiencias.map((e, i) => (
              <ExperienceRow key={`r-${i}`} exp={e} diff={{ state: "removed", fields: [] }} />
            ))}
          </div>
        </Section>

        {/* Skills */}
        <Section
          title={
            readOnly
              ? `Skills (${totalSkills})`
              : `Skills (${includedSkillCount} de ${totalSkills} selecionadas)`
          }
          {...secao("skills")}
        >
          {!isFirstUpload && diff && (diff.added.skills.length > 0 || diff.removed.skills.length > 0) && (
            <p className="text-xs text-muted-foreground mb-2 -mt-1">
              Verde: nova no LinkedIn. Vermelho riscado: saiu do LinkedIn.
            </p>
          )}
          <div className="flex flex-wrap gap-1.5">
            {extracted.skills.map((s, i) => {
              const key = slugify(s.nome);
              return (
                <SkillChip
                  key={i}
                  skill={s}
                  state={skillState.get(key)}
                  excluded={readOnly ? false : excludedSkillKeys.has(key)}
                  onToggleExclude={readOnly ? undefined : () => toggleSkill(key)}
                />
              );
            })}
            {diff?.removed.skills.map((s, i) => (
              <SkillChip key={`r-${i}`} skill={s} state="removed" />
            ))}
          </div>
        </Section>

        {/* Formação */}
        {extracted.formacao.length > 0 && (
          <Section title="Formação" {...secao("formacao")}>
            <ul className="space-y-1 text-sm">
              {extracted.formacao.map((f, i) => (
                <li key={i}>
                  <span className="font-medium">{f.instituicao}</span>
                  {f.curso && <span className="text-muted-foreground"> · {f.curso}</span>}
                  {f.periodo && <span className="text-muted-foreground ml-1">({f.periodo})</span>}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      {mostraChecklist && (
        <>
          {slotDaChecklist &&
            createPortal(
              <ChecklistDaRevisao itens={itensRevisao} contexto={contextoRevisao} onIr={irPara} modo="coluna" />,
              slotDaChecklist,
            )}
          <div className={cn(slotDaChecklist && "xl:hidden")}>
            <ChecklistDaRevisao itens={itensRevisao} contexto={contextoRevisao} onIr={irPara} modo="flutuante" />
          </div>
        </>
      )}

      {/* Footer (sticky) */}
      {readOnly ? (
        <div className="sticky bottom-0 bg-background border-t pt-4 mt-2 flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </div>
      ) : (
      <div className="sticky bottom-0 bg-background border-t pt-4 mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
        {hasOverrides ? (
          <span className="text-xs text-muted-foreground">
            {includedExpCount} de {totalExps} experiência{totalExps === 1 ? "" : "s"} ·{" "}
            {includedSkillCount} de {totalSkills} skill{totalSkills === 1 ? "" : "s"}
          </span>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          {sequential ? (
            <Button variant="secondary" onClick={sequential.onSkip} disabled={apply.isPending}>
              Pular
            </Button>
          ) : (
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
          )}
          <Button
            onClick={() => apply.mutate()}
            disabled={apply.isPending || pendentes.length > 0}
            title={
              pendentes.length > 0
                ? `Falta: ${pendentes.map((item) => item.rotulo).join(", ")}`
                : undefined
            }
          >
            {apply.isPending
              ? "Aplicando..."
              : sequential
              ? sequential.current < sequential.total
                ? "Aplicar e Próximo"
                : "Aplicar e Finalizar"
              : hasOverrides
              ? "Aplicar selecionados"
              : "Aplicar ao Freelancer"}
          </Button>
        </div>
      </div>
      )}
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

interface SectionProps {
  id?: string;
  title: string;
  /** Situação do ponto que a seção resolve, ao lado do título. */
  status?: "pendente" | "respondida";
  /** Sem controle (modo visualização), a seção fica sempre aberta. */
  aberta?: boolean;
  onAbertaChange?: (aberta: boolean) => void;
  revisada?: boolean;
  onRevisadaChange?: (revisada: boolean) => void;
  /** Por que a seção começa fechada (ex.: "nada mudou"). */
  aviso?: string;
  /** A IA aplicou a seção sozinha e ninguém mexeu no "Revisado" ainda. */
  pelaIa?: boolean;
  children: React.ReactNode;
}

/**
 * Seção do formulário: abre e fecha pelo título; "Revisado" fecha e marca, como "Viewed" num diff.
 * O que a IA aplicou sozinha mostra "pela IA" no lugar; clicar passa a ser revisão de quem confere.
 */
function Section({ id, title, status, aberta, onAbertaChange, revisada, onRevisadaChange, aviso, pelaIa, children }: SectionProps) {
  const recolhivel = !!onAbertaChange;
  const mostraConteudo = !recolhivel || aberta;
  return (
    <div id={id} className="scroll-mt-4">
      <div className={cn("flex items-center gap-2", mostraConteudo && "mb-2")}>
        {recolhivel ? (
          <button
            type="button"
            onClick={() => onAbertaChange(!aberta)}
            className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
            aria-expanded={aberta}
          >
            <ChevronRight className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", aberta && "rotate-90")} />
            <h3 className={cn("text-sm font-semibold", revisada && "text-muted-foreground")}>{title}</h3>
            {!aberta && aviso && <span className="truncate text-xs text-muted-foreground">· {aviso}</span>}
          </button>
        ) : (
          <h3 className="flex-1 text-sm font-semibold">{title}</h3>
        )}
        {status === "pendente" && (
          <Badge variant="outline" className="text-[10px]">resposta pendente</Badge>
        )}
        {status === "respondida" && <span className="text-xs text-green-700">✓ respondida</span>}
        {onRevisadaChange && pelaIa && (
          <button
            type="button"
            onClick={() => onRevisadaChange(true)}
            className="flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/20"
            title="A IA aplicou esta seção sozinha. Clique para marcar como revisada por você."
          >
            <Sparkles className="h-3.5 w-3.5" />
            pela IA
          </button>
        )}
        {onRevisadaChange && !pelaIa && (
          <label className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
            <Checkbox checked={revisada} onCheckedChange={(marcado) => onRevisadaChange(marcado === true)} />
            Revisado
          </label>
        )}
      </div>
      {mostraConteudo && children}
      <Separator className="mt-4" />
    </div>
  );
}

/** Uma posição atual com o que ajuda a dizer se é emprego, freela ou empresa própria. */
function PosicaoAtual({ experiencia }: { experiencia: ExperienceItem }) {
  const website = experiencia.empresa_website?.trim();
  const websiteHref = website ? (website.startsWith("http") ? website : `https://${website}`) : null;
  const empresaMeta = [experiencia.empresa_tipo, experiencia.empresa_segmento_inferido].filter(Boolean);
  const linkedinDaEmpresa = linkHttp(experiencia.empresa_linkedin_url);
  return (
    <div className="rounded-md border px-3 py-2 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-2">
        <span className="font-medium">{experiencia.cargo_nome}</span>
        {experiencia.vinculo_tipo && (
          <span className="text-xs text-muted-foreground">IA: {VINCULO_LABELS[experiencia.vinculo_tipo]}</span>
        )}
      </div>
      <div className="text-muted-foreground">
        {experiencia.empresa_nome}
        <span className="text-xs"> · desde {parsePartialDate(experiencia.data_inicio)}</span>
      </div>
      {(empresaMeta.length > 0 || websiteHref || linkedinDaEmpresa) && (
        <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-muted-foreground">
          {empresaMeta.length > 0 && <span>{empresaMeta.join(" · ")}</span>}
          {websiteHref && (
            <a href={websiteHref} target="_blank" rel="noreferrer" className="text-primary hover:underline">
              {website}
            </a>
          )}
          {linkedinDaEmpresa && (
            <a href={linkedinDaEmpresa} target="_blank" rel="noreferrer" className="text-primary hover:underline">
              LinkedIn da empresa
            </a>
          )}
        </div>
      )}
      {experiencia.empresa_descricao && (
        <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{experiencia.empresa_descricao}</p>
      )}
    </div>
  );
}

type DiffState = "new" | "changed" | "removed" | "unchanged";

/** O que o diff sabe sobre uma linha: estado, campos do perfil que mudaram e a versão anterior. */
interface ExpDiffInfo {
  state: DiffState;
  fields: string[];
  before?: ExperienceItem;
}

/** Campos por experiência que a IA infere/reescreve entre rodadas: nunca viram selo. */
const EXP_INTERNAL_FIELDS = new Set(["descricao", "vinculo_tipo", "empresa_reconhecida_publicamente"]);

const EXP_FIELD_LABELS: Record<string, string> = {
  cargo: "cargo",
  empresa: "nome da empresa",
  data_inicio: "data de início",
  data_fim: "data de término",
  is_atual: "emprego atual",
  localizacao: "local",
};

/** Campos de topo do perfil recalculados pela IA: nunca aparecem como mudança. */
const PROFILE_INTERNAL_FIELDS = new Set([
  "linkedin_url",
  "senioridade_global_inferida",
  "anos_experiencia_total",
  "is_freelancer_declarado",
]);

const PROFILE_FIELD_LABELS: Record<string, string> = {
  headline: "Headline",
  resumo: "Resumo",
  localizacao: "Localização",
};

function formatProfileValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "(vazio)";
  return String(value);
}

function formatExpFieldValue(field: string, exp: ExperienceItem): string {
  switch (field) {
    case "cargo":
      return exp.cargo_nome;
    case "empresa":
      return exp.empresa_nome;
    case "data_inicio":
      return exp.data_inicio ? parsePartialDate(exp.data_inicio) : "?";
    case "data_fim":
      return exp.is_atual ? "presente" : exp.data_fim ? parsePartialDate(exp.data_fim) : "?";
    case "is_atual":
      return exp.is_atual ? "sim" : "não";
    case "localizacao":
      return exp.localizacao?.trim() || "(vazio)";
    default:
      return String((exp as unknown as Record<string, unknown>)[field] ?? "(vazio)");
  }
}

/** Valor anterior riscado em vermelho, valor novo em verde; texto longo é cortado com o inteiro no title. */
function BeforeAfter({ label, before, after }: { label: string; before: string; after: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-1.5 text-xs">
      <span className="text-muted-foreground">{label}:</span>
      <span className="inline-block max-w-[18rem] truncate align-bottom text-red-700 line-through" title={before}>
        {before}
      </span>
      <span className="inline-block max-w-[18rem] truncate align-bottom text-green-700" title={after}>
        {after}
      </span>
    </div>
  );
}

/** Decisão do revisor sobre o "atual?" de uma experiência (override do apply). */
interface ExpOverride {
  is_atual: boolean;
  /** "YYYY-MM" quando encerrou; null quando é o atual. */
  data_fim: string | null;
}

function ExperienceRow({
  id,
  exp,
  diff,
  excluded,
  onToggleExclude,
  empresaNoBanco,
  override,
  onSetOverride,
}: {
  id?: string;
  exp: ExperienceItem;
  diff?: ExpDiffInfo;
  excluded?: boolean;
  onToggleExclude?: () => void;
  empresaNoBanco?: boolean;
  override?: ExpOverride;
  onSetOverride?: (o: ExpOverride) => void;
}) {
  // Estado efetivo: override do revisor (se houver) ganha do que a IA extraiu.
  const isAtual = override ? override.is_atual : exp.is_atual;
  const dataFim = override ? override.data_fim : exp.data_fim;
  const periodo = formatPeriodo(exp.data_inicio, dataFim, isAtual);
  // Editor inline aparece em experiências que estão (ou foram inferidas como) o
  // emprego atual — são as candidatas a "presente vs. data de término".
  const showAtualEditor =
    !!onSetOverride && (isAtual || exp.is_atual || exp.is_atual_a_confirmar || !!override);
  const needsConfirm = exp.is_atual_a_confirmar && !override;
  const empresaMeta = [
    exp.empresa_tipo,
    exp.empresa_segmento_inferido,
    [exp.empresa_cidade, exp.empresa_pais].filter(Boolean).join(", ") || null,
  ].filter(Boolean) as string[];
  const website = exp.empresa_website?.trim();
  const websiteHref = website
    ? website.startsWith("http")
      ? website
      : `https://${website}`
    : null;
  const state = diff?.state;
  const changedFields = diff?.fields ?? [];
  const stateBadge =
    state === "new" ? (
      <Badge className="bg-green-100 text-green-800 text-xs">Nova no LinkedIn</Badge>
    ) : state === "changed" ? (
      <Badge className="bg-yellow-100 text-yellow-800 text-xs">
        Mudou: {changedFields.map((field) => EXP_FIELD_LABELS[field] ?? field).join(", ")}
      </Badge>
    ) : state === "removed" ? (
      <Badge className="bg-red-100 text-red-800 text-xs">Saiu do LinkedIn</Badge>
    ) : null;
  const isRemoved = state === "removed";
  const isExcluded = !!excluded;
  return (
    <div
      id={id}
      className={cn(
        "border rounded p-3 flex items-start gap-3",
        isRemoved && "opacity-60 line-through",
        isExcluded && "opacity-50 line-through",
      )}
    >
      {onToggleExclude && (
        <Checkbox
          checked={!isExcluded}
          onCheckedChange={onToggleExclude}
          className="mt-0.5"
          aria-label={`Incluir experiência em ${exp.empresa_nome}`}
        />
      )}
      <div className="flex-1">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-medium text-sm">{exp.empresa_nome}</span>
              {!isRemoved &&
                (exp.empresa_registravel === false ? (
                  <Badge
                    variant="outline"
                    className="text-[10px] border-amber-300 text-amber-700"
                    title={
                      exp.empresa_registravel_motivo ??
                      "A IA avaliou que este texto não nomeia uma empresa de verdade"
                    }
                  >
                    não vira empresa cadastrada
                  </Badge>
                ) : empresaNoBanco ? (
                  <Badge
                    variant="outline"
                    className="text-[10px] border-transparent bg-muted text-muted-foreground"
                  >
                    no banco
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="text-[10px] border-green-300 text-green-700"
                  >
                    nova empresa
                  </Badge>
                ))}
            </div>
            {exp.empresa_registravel === false && exp.empresa_registravel_motivo && (
              <div className="text-[11px] text-amber-700">
                {exp.empresa_registravel_motivo}
              </div>
            )}
            <div className="text-sm text-muted-foreground">{exp.cargo_nome}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{periodo}</div>
            {state === "changed" && diff?.before && changedFields.length > 0 && (
              <div className="mt-1 space-y-0.5 rounded bg-yellow-50 px-2 py-1">
                {changedFields.map((field) => (
                  <BeforeAfter
                    key={field}
                    label={EXP_FIELD_LABELS[field] ?? field}
                    before={formatExpFieldValue(field, diff.before!)}
                    after={formatExpFieldValue(field, exp)}
                  />
                ))}
              </div>
            )}
            {(empresaMeta.length > 0 || websiteHref || exp.empresa_descricao) && (
              <div className="mt-1 space-y-0.5">
                {empresaMeta.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    🏢 {empresaMeta.join(" · ")}
                  </div>
                )}
                {websiteHref && (
                  <a
                    href={websiteHref}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary underline hover:no-underline break-all"
                    onClick={(ev) => ev.stopPropagation()}
                  >
                    {website}
                  </a>
                )}
                {exp.empresa_descricao && (
                  <div className="text-xs text-muted-foreground line-clamp-2">
                    {exp.empresa_descricao}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Sem editor (ex.: linha "removida"): mantém só o selo informativo. */}
            {!showAtualEditor && isAtual && (
              <Badge variant="outline" className="text-xs">
                atual
              </Badge>
            )}
            {stateBadge}
          </div>
        </div>
        {showAtualEditor && onSetOverride && (
          <AtualEditor
            isAtual={isAtual}
            dataFim={dataFim}
            dataInicio={exp.data_inicio}
            needsConfirm={!!needsConfirm}
            confirmed={!!override}
            onSetOverride={onSetOverride}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Controle inline pra confirmar/editar se a experiência é o emprego atual
 * ("presente") ou já encerrou (com data de término). Substitui o antigo selo
 * passivo "atual? confirmar" — agora a decisão é registrada como override.
 */
function AtualEditor({
  isAtual,
  dataFim,
  dataInicio,
  needsConfirm,
  confirmed,
  onSetOverride,
}: {
  isAtual: boolean;
  dataFim: string | null;
  dataInicio: string;
  needsConfirm: boolean;
  confirmed: boolean;
  onSetOverride: (o: ExpOverride) => void;
}) {
  const setAtual = () => onSetOverride({ is_atual: true, data_fim: null });
  const setEncerrou = () =>
    onSetOverride({ is_atual: false, data_fim: dataFim || defaultEndMonth(dataInicio) });

  return (
    <div
      className={cn(
        "mt-2 rounded-md border px-2.5 py-2 text-xs",
        needsConfirm ? "border-amber-300 bg-amber-50" : "border-muted bg-muted/40",
      )}
    >
      <div className="mb-1.5 font-medium">
        {needsConfirm ? (
          <span className="text-amber-800">
            Ainda é o emprego atual? (o PDF não disse "Presente"; inferimos pela
            duração)
          </span>
        ) : (
          <span className="text-muted-foreground">Este é o emprego atual?</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex overflow-hidden rounded-md border">
          <button
            type="button"
            onClick={setAtual}
            className={cn(
              "px-2.5 py-1 transition-colors",
              isAtual
                ? "bg-primary text-primary-foreground"
                : "bg-background hover:bg-muted",
            )}
          >
            Sim, é o atual
          </button>
          <button
            type="button"
            onClick={setEncerrou}
            className={cn(
              "border-l px-2.5 py-1 transition-colors",
              !isAtual
                ? "bg-primary text-primary-foreground"
                : "bg-background hover:bg-muted",
            )}
          >
            Não, já encerrou
          </button>
        </div>
        {!isAtual && (
          <label className="flex items-center gap-1.5 text-muted-foreground">
            Encerrou em
            <input
              type="month"
              value={toMonthInput(dataFim)}
              max={defaultEndMonth(dataInicio) /* não deixa data futura */}
              onChange={(ev) =>
                onSetOverride({ is_atual: false, data_fim: ev.target.value || null })
              }
              className="rounded border bg-background px-1.5 py-0.5"
            />
          </label>
        )}
        {confirmed && <span className="text-green-700">✓ confirmado</span>}
      </div>
    </div>
  );
}

function SkillChip({
  skill,
  state,
  excluded,
  onToggleExclude,
}: {
  skill: SkillItem;
  state?: DiffState;
  excluded?: boolean;
  onToggleExclude?: () => void;
}) {
  const cls =
    state === "new"
      ? "bg-green-100 text-green-800"
      : state === "removed"
      ? "bg-red-100 text-red-800 line-through"
      : "";
  const content = (
    <Badge
      variant={skill.is_principal ? "default" : "outline"}
      className={cn(
        "text-xs",
        cls,
        excluded && "opacity-50 line-through",
        onToggleExclude && "cursor-pointer select-none",
      )}
      onClick={onToggleExclude}
      title={
        onToggleExclude
          ? excluded
            ? "Clique para incluir"
            : "Clique para excluir"
          : undefined
      }
    >
      {skill.nome}
      {skill.categoria_inferida && (
        <span className="ml-1 opacity-60">· {skill.categoria_inferida}</span>
      )}
    </Badge>
  );
  return content;
}

/** O endereço só vira link quando é http(s): o valor vem da leitura da IA. */
function linkHttp(valor: string | null | undefined): string | null {
  try {
    const url = new URL(valor ?? "");
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : null;
  } catch {
    return null;
  }
}

function formatPeriodo(inicio: string, fim: string | null, atual: boolean): string {
  const start = parsePartialDate(inicio);
  const end = atual ? "presente" : fim ? parsePartialDate(fim) : "?";
  const months = calcMonths(inicio, fim, atual);
  return `${start} – ${end} · ${formatDuration(months)}`;
}

function parsePartialDate(s: string): string {
  // "2017-09" → "set/2017"; "2017" → "2017"
  const m = s.match(/^(\d{4})(?:-(\d{2}))?/);
  if (!m) return s;
  const [, y, mm] = m;
  if (!mm) return y;
  const meses = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${meses[parseInt(mm, 10) - 1]}/${y}`;
}

/** Normaliza "YYYY", "YYYY-MM", "YYYY-MM-DD" → "YYYY-MM" pro <input type="month">. */
function toMonthInput(s: string | null): string {
  if (!s) return "";
  const m = s.match(/^(\d{4})(?:-(\d{2}))?/);
  if (!m) return "";
  return `${m[1]}-${m[2] ?? "01"}`;
}

/** Mês default ao marcar "encerrou": o mês atual (nunca antes do início). */
function defaultEndMonth(inicio: string): string {
  const now = new Date();
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const start = toMonthInput(inicio);
  return start && start > current ? start : current;
}

function calcMonths(inicio: string, fim: string | null, atual: boolean): number {
  const parse = (s: string) => {
    const m = s.match(/^(\d{4})(?:-(\d{2}))?/);
    if (!m) return null;
    return { y: parseInt(m[1]), m: parseInt(m[2] ?? "1") };
  };
  const start = parse(inicio);
  const end = atual ? { y: new Date().getFullYear(), m: new Date().getMonth() + 1 } : fim ? parse(fim) : null;
  if (!start || !end) return 0;
  return (end.y - start.y) * 12 + (end.m - start.m) + 1;
}

function formatDuration(months: number): string {
  if (months <= 0) return "—";
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m} ${m === 1 ? "mês" : "meses"}`;
  if (m === 0) return `${y} ${y === 1 ? "ano" : "anos"}`;
  return `${y}a ${m}m`;
}

function sortByDate(exps: ExperienceItem[]): ExperienceItem[] {
  return [...exps].sort((a, b) => (b.data_inicio > a.data_inicio ? 1 : -1));
}

function expKey(e: ExperienceItem): string {
  return `${slugify(e.empresa_nome)}|${slugify(e.cargo_nome)}|${e.data_inicio}`;
}

function slugify(s: string): string {
  return s
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// --- Trajetória profissional -------------------------------------------------

const PADRAO_CARREIRA_LABELS: Record<string, string> = {
  estavel_longa_permanencia: "Carreira estável (longas permanências)",
  movimento_frequente: "Troca de empresa com frequência",
  freelancer_recorrente: "Freelancer recorrente",
  progressao_ascendente: "Progressão ascendente",
};

function padraoCarreiraLabel(value: string): string {
  return PADRAO_CARREIRA_LABELS[value] ?? value.replace(/_/g, " ");
}

/** Meses → texto legível em pt-BR: "1 ano e 6 meses", "8 anos", "5 meses". */
function formatMeses(value: number): string {
  const months = Math.round(value);
  if (months <= 0) return "—";
  const anos = Math.floor(months / 12);
  const meses = months % 12;
  const parteAnos = anos > 0 ? `${anos} ${anos === 1 ? "ano" : "anos"}` : "";
  const parteMeses = meses > 0 ? `${meses} ${meses === 1 ? "mês" : "meses"}` : "";
  if (parteAnos && parteMeses) return `${parteAnos} e ${parteMeses}`;
  return parteAnos || parteMeses;
}

function hasTenure(c: CarreiraObservacoes): boolean {
  return (
    c.tenure_medio_meses != null ||
    c.tenure_maior_meses != null ||
    c.tenure_atual_meses != null
  );
}

function TenureStat({ label, meses }: { label: string; meses: number | null }) {
  return (
    <div className="space-y-0.5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">
        {meses != null ? formatMeses(meses) : "—"}
      </div>
    </div>
  );
}

// --- Áreas: reconciliação com o catálogo (antes/depois) ----------------------

interface AreaItem {
  raw: string;
  canonical: string;
  inCatalog: boolean;
  already: boolean;
}

interface AreasView {
  current: string[];
  itens: AreaItem[];
  toApply: string[];
  toApplyNormSet: Set<string>;
  final: string[];
  hasOffCatalog: boolean;
}

/**
 * Casa as áreas sugeridas pela IA com o catálogo canônico (areas_atuacao) e com
 * as áreas atuais do freelancer. Só áreas do catálogo (e ainda não cadastradas)
 * entram em `toApply`/`final`. Sem catálogo carregado, cai no comportamento
 * antigo (trata tudo como válido) pra não engolir sugestões silenciosamente.
 */
function computeAreasView(
  suggested: string[],
  current: string[],
  catalog: string[],
): AreasView {
  const catalogByNorm = new Map(catalog.map((n) => [slugify(n), n]));
  const catalogLoaded = catalog.length > 0;
  const currentNorm = new Set(current.map(slugify));

  const seen = new Set<string>();
  const itens: AreaItem[] = [];
  for (const s of suggested) {
    const n = slugify(s);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    const canonicalFromCatalog = catalogByNorm.get(n);
    const inCatalog = catalogLoaded ? !!canonicalFromCatalog : true;
    const canonical = canonicalFromCatalog ?? s;
    const already = currentNorm.has(n) || currentNorm.has(slugify(canonical));
    itens.push({ raw: s, canonical, inCatalog, already });
  }

  const toApply: string[] = [];
  const toApplyNormSet = new Set<string>();
  for (const it of itens) {
    const cn = slugify(it.canonical);
    if (it.inCatalog && !it.already && !toApplyNormSet.has(cn)) {
      toApply.push(it.canonical);
      toApplyNormSet.add(cn);
    }
  }

  return {
    current,
    itens,
    toApply,
    toApplyNormSet,
    final: [...current, ...toApply],
    hasOffCatalog: itens.some((it) => !it.inCatalog),
  };
}

/**
 * Estado de cada experiência pela chave. No primeiro upload nada ganha selo
 * (tudo é novo por definição). `fields` de jobs anteriores à COR-367 ainda
 * trazem campos internos: são filtrados aqui, e sem campo visível não há selo.
 */
function computeExpState(job: LinkedInJob | null): Map<string, ExpDiffInfo> {
  const map = new Map<string, ExpDiffInfo>();
  const diff = job?.diff_summary;
  if (!diff || diff.is_first) return map;
  for (const e of diff.added.experiencias) {
    map.set(expKey(e), { state: "new", fields: [] });
  }
  for (const c of diff.changed) {
    const fields = c.fields.filter((field) => !EXP_INTERNAL_FIELDS.has(field));
    if (fields.length === 0) continue;
    map.set(expKey(c.experiencia), { state: "changed", fields, before: c.before });
  }
  return map;
}

function computeSkillState(job: LinkedInJob | null): Map<string, DiffState> {
  const map = new Map<string, DiffState>();
  if (!job?.diff_summary || job.diff_summary.is_first) return map;
  for (const s of job.diff_summary.added.skills) {
    map.set(slugify(s.nome), "new");
  }
  return map;
}
